import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Trophy, Plus, Trash2, Pencil, Table as TableIcon, Award, Save, Shuffle } from "lucide-react";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  listInternalChampionships,
  upsertInternalChampionship,
  deleteInternalChampionship,
} from "@/lib/api/internal-championships.functions";

export const Route = createFileRoute("/_authenticated/m/gerenciar-campeonato")({
  head: () => ({
    meta: [
      { title: "Gerenciar Campeonato" },
      { name: "description", content: "Gerencie campeonatos internos, jogos e classificações" },
    ],
  }),
  component: GerenciarCampeonatoPage,
});

const EVENT_TYPES = ["Futsal / Salão", "Futebol Society", "Futebol de Campo"];

type Team = { id: string; name: string };
type Match = {
  id: string;
  home_id: string;
  away_id: string;
  home_score: number | null;
  away_score: number | null;
  home_yellow: number;
  away_yellow: number;
  home_red: number;
  away_red: number;
  played: boolean;
  match_date?: string | null;
  round?: number | null;
};
type FormState = {
  id: string | null;
  name: string;
  event_type: string;
  format: "pontos_corridos" | "grupo_unico";
  finalists_per_bracket: number;
  teams: Team[];
  matches: Match[];
  finals: Record<string, any>;
  notes: string;
};

const uid = () => Math.random().toString(36).slice(2, 10);

const emptyForm = (): FormState => ({
  id: null,
  name: "",
  event_type: "Futsal / Salão",
  format: "grupo_unico",
  finalists_per_bracket: 2,
  teams: [],
  matches: [],
  finals: {},
  notes: "",
});

function generateRoundRobin(teams: Team[]): Match[] {
  const ids = teams.map((t) => t.id);
  const matches: Match[] = [];
  for (let i = 0; i < ids.length; i++) {
    for (let j = i + 1; j < ids.length; j++) {
      matches.push({
        id: uid(),
        home_id: ids[i],
        away_id: ids[j],
        home_score: null,
        away_score: null,
        home_yellow: 0,
        away_yellow: 0,
        home_red: 0,
        away_red: 0,
        played: false,
      });
    }
  }
  return matches;
}

type Standing = {
  team_id: string;
  name: string;
  played: number;
  wins: number;
  draws: number;
  losses: number;
  gf: number;
  ga: number;
  gd: number;
  points: number;
  yellow: number;
  red: number;
};

function computeStandings(teams: Team[], matches: Match[]): Standing[] {
  const map = new Map<string, Standing>();
  for (const t of teams) {
    map.set(t.id, {
      team_id: t.id, name: t.name,
      played: 0, wins: 0, draws: 0, losses: 0,
      gf: 0, ga: 0, gd: 0, points: 0, yellow: 0, red: 0,
    });
  }
  for (const m of matches) {
    if (!m.played) continue;
    const h = map.get(m.home_id); const a = map.get(m.away_id);
    if (!h || !a) continue;
    const hs = m.home_score ?? 0; const as = m.away_score ?? 0;
    h.played++; a.played++;
    h.gf += hs; h.ga += as; a.gf += as; a.ga += hs;
    h.yellow += m.home_yellow || 0; a.yellow += m.away_yellow || 0;
    h.red += m.home_red || 0; a.red += m.away_red || 0;
    if (hs > as) { h.wins++; h.points += 3; a.losses++; }
    else if (hs < as) { a.wins++; a.points += 3; h.losses++; }
    else { h.draws++; a.draws++; h.points++; a.points++; }
  }
  const out = Array.from(map.values());
  for (const s of out) s.gd = s.gf - s.ga;
  out.sort((x, y) =>
    y.points - x.points ||
    y.wins - x.wins ||
    y.gd - x.gd ||
    y.gf - x.gf ||
    x.red - y.red ||
    x.yellow - y.yellow ||
    x.name.localeCompare(y.name)
  );
  return out;
}

function buildBrackets(standings: Standing[], finalistsPerBracket: number) {
  const size = finalistsPerBracket;
  const brackets: { label: string; teams: Standing[] }[] = [];
  const labels = ["🥇 Final Ouro", "🥈 Final Prata", "🥉 Final Bronze"];
  for (let i = 0; i < 3; i++) {
    const slice = standings.slice(i * size, (i + 1) * size);
    if (slice.length > 0) brackets.push({ label: labels[i], teams: slice });
  }
  return brackets;
}

function GerenciarCampeonatoPage() {
  const listFn = useServerFn(listInternalChampionships);
  const saveFn = useServerFn(upsertInternalChampionship);
  const delFn = useServerFn(deleteInternalChampionship);

  const [tab, setTab] = useState("criar");
  const [items, setItems] = useState<any[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [standingsOpen, setStandingsOpen] = useState(false);

  const reload = async () => {
    try { const r = await listFn(); setItems(r.items); }
    catch (e: any) { toast.error(e.message ?? "Erro ao carregar"); }
  };
  useEffect(() => { reload(); }, []);

  const upd = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const setTeamCount = (n: number) => {
    const cur = form.teams;
    const next = n > cur.length
      ? [...cur, ...Array.from({ length: n - cur.length }, (_, i) =>
          ({ id: uid(), name: `Time ${cur.length + i + 1}` }))]
      : cur.slice(0, n);
    setForm((p) => ({ ...p, teams: next, matches: [] }));
  };

  const renameTeam = (i: number, name: string) =>
    upd("teams", form.teams.map((t, idx) => idx === i ? { ...t, name } : t));

  const removeTeam = (i: number) => {
    const removed = form.teams[i];
    setForm((p) => ({
      ...p,
      teams: p.teams.filter((_, idx) => idx !== i),
      matches: p.matches.filter((m) => m.home_id !== removed.id && m.away_id !== removed.id),
    }));
  };

  const generateMatches = () => {
    if (form.teams.length < 2) return toast.error("Cadastre pelo menos 2 times");
    if (form.teams.some((t) => !t.name.trim())) return toast.error("Nomeie todos os times");
    upd("matches", generateRoundRobin(form.teams));
    toast.success(`Tabela gerada: ${form.teams.length * (form.teams.length - 1) / 2} jogos`);
  };

  const addMatch = () => {
    if (form.teams.length < 2) return toast.error("Cadastre pelo menos 2 times");
    setForm((p) => ({
      ...p,
      matches: [...p.matches, {
        id: uid(),
        home_id: p.teams[0].id,
        away_id: p.teams[1].id,
        home_score: null, away_score: null,
        home_yellow: 0, away_yellow: 0, home_red: 0, away_red: 0,
        played: false,
      }],
    }));
  };

  const updMatch = (id: string, patch: Partial<Match>) =>
    upd("matches", form.matches.map((m) => m.id === id ? { ...m, ...patch } : m));

  const removeMatch = (id: string) =>
    upd("matches", form.matches.filter((m) => m.id !== id));

  const teamName = (id: string) => form.teams.find((t) => t.id === id)?.name ?? "—";

  // Contagem de jogos por time (para validar antes de salvar)
  const gamesPerTeam = useMemo(() => {
    const c = new Map<string, number>();
    form.teams.forEach((t) => c.set(t.id, 0));
    form.matches.forEach((m) => {
      c.set(m.home_id, (c.get(m.home_id) ?? 0) + 1);
      c.set(m.away_id, (c.get(m.away_id) ?? 0) + 1);
    });
    return c;
  }, [form.teams, form.matches]);

  const allTeamsSameCount = useMemo(() => {
    if (form.teams.length === 0) return true;
    const counts = Array.from(gamesPerTeam.values());
    return counts.every((n) => n === counts[0]);
  }, [gamesPerTeam, form.teams.length]);

  const standings = useMemo(
    () => computeStandings(form.teams, form.matches),
    [form.teams, form.matches]
  );
  const brackets = useMemo(
    () => buildBrackets(standings, form.finalists_per_bracket),
    [standings, form.finalists_per_bracket]
  );

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Informe o nome do campeonato");
    if (form.matches.length > 0 && !allTeamsSameCount) {
      return toast.error("Todos os times precisam ter a mesma quantidade de jogos");
    }
    try {
      const r = await saveFn({ data: { ...form, id: form.id ?? undefined } as any });
      toast.success("Campeonato salvo");
      setForm((p) => ({ ...p, id: r.id }));
      reload();
    } catch (e: any) { toast.error(e.message ?? "Erro ao salvar"); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Excluir este campeonato?")) return;
    try { await delFn({ data: { id } }); toast.success("Excluído"); reload(); }
    catch (e: any) { toast.error(e.message ?? "Erro ao excluir"); }
  };

  const editItem = (it: any) => {
    setForm({
      id: it.id,
      name: it.name ?? "",
      event_type: it.event_type ?? "Futsal / Salão",
      format: (it.format as any) ?? "grupo_unico",
      finalists_per_bracket: it.finalists_per_bracket ?? 2,
      teams: Array.isArray(it.teams) ? it.teams : [],
      matches: Array.isArray(it.matches) ? it.matches : [],
      finals: it.finals ?? {},
      notes: it.notes ?? "",
    });
    setTab("criar");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Trophy className="h-7 w-7 text-primary" />
        <h1 className="text-3xl font-bold tracking-tight">Gerenciar Campeonato</h1>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="criar">{form.id ? "Editar" : "Criar"}</TabsTrigger>
          <TabsTrigger value="lista">Campeonatos ({items.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="criar" className="mt-4 space-y-4">
          <Card>
            <CardHeader><CardTitle>{form.id ? "Editar Campeonato" : "Novo Campeonato"}</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-3 gap-4">
                <Field label="Nome do Campeonato *">
                  <Input value={form.name} onChange={(e) => upd("name", e.target.value)} />
                </Field>
                <Field label="Tipo">
                  <Select value="Campeonato" onValueChange={() => {}}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Campeonato">Campeonato</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Modalidade">
                  <Select value={form.event_type} onValueChange={(v) => upd("event_type", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <fieldset className="border rounded-md p-4 space-y-3">
                <legend className="px-2 text-sm font-semibold">Forma de Disputa</legend>
                <Select value={form.format} onValueChange={(v) => upd("format", v as any)}>
                  <SelectTrigger className="max-w-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="grupo_unico">Grupo Único (com finais)</SelectItem>
                    <SelectItem value="pontos_corridos">Pontos Corridos</SelectItem>
                  </SelectContent>
                </Select>

                {form.format === "grupo_unico" && (
                  <div className="grid md:grid-cols-2 gap-4 mt-3">
                    <Field label="Finalistas por chave (2 ou 4)">
                      <Select
                        value={String(form.finalists_per_bracket)}
                        onValueChange={(v) => upd("finalists_per_bracket", Number(v))}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="2">2 por chave</SelectItem>
                          <SelectItem value="4">4 por chave</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    <div className="text-xs text-muted-foreground self-end pb-2">
                      {form.finalists_per_bracket === 2
                        ? "🥇 Ouro (1º–2º) · 🥈 Prata (3º–4º) · 🥉 Bronze (5º–6º)"
                        : "🥇 Ouro (1º–4º) · 🥈 Prata (5º–8º) · 🥉 Bronze (9º–12º)"}
                    </div>
                  </div>
                )}

                {form.format === "pontos_corridos" && (
                  <p className="text-xs text-muted-foreground">
                    Campeão = time com maior pontuação. Desempate: vitórias, saldo, gols pró,
                    menos vermelhos, menos amarelos.
                  </p>
                )}
              </fieldset>

              <fieldset className="border rounded-md p-4 space-y-3">
                <legend className="px-2 text-sm font-semibold">Times</legend>
                <div className="flex items-end gap-2 max-w-md">
                  <div className="flex-1">
                    <Label className="text-xs">Quantidade de Times</Label>
                    <Input
                      type="number" min={0}
                      value={form.teams.length}
                      onChange={(e) => setTeamCount(Math.max(0, Number(e.target.value) || 0))}
                    />
                  </div>
                  <Button type="button" variant="outline"
                    onClick={() => setForm((p) => ({ ...p, teams: [...p.teams, { id: uid(), name: `Time ${p.teams.length + 1}` }] }))}>
                    <Plus className="h-4 w-4 mr-1" />Adicionar Time
                  </Button>
                </div>
                <div className="grid md:grid-cols-2 gap-2">
                  {form.teams.map((t, i) => (
                    <div key={t.id} className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-6">{i + 1}º</span>
                      <Input value={t.name} onChange={(e) => renameTeam(i, e.target.value)} />
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeTeam(i)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              </fieldset>

              <fieldset className="border rounded-md p-4 space-y-3">
                <legend className="px-2 text-sm font-semibold">Tabela de Jogos</legend>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="default" onClick={generateMatches}>
                    <Shuffle className="h-4 w-4 mr-1" />Gerar Todos x Todos
                  </Button>
                  <Button type="button" variant="outline" onClick={addMatch}>
                    <Plus className="h-4 w-4 mr-1" />Adicionar Jogo
                  </Button>
                  {!allTeamsSameCount && form.matches.length > 0 && (
                    <Badge variant="destructive">
                      Times com quantidade desigual de jogos
                    </Badge>
                  )}
                </div>

                {form.matches.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Nenhum jogo cadastrado. Clique em "Gerar Todos x Todos" para criar a tabela automaticamente.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-8">#</TableHead>
                          <TableHead>Mandante</TableHead>
                          <TableHead className="w-20">Placar</TableHead>
                          <TableHead>Visitante</TableHead>
                          <TableHead className="w-20">🟨/🟥 M</TableHead>
                          <TableHead className="w-20">🟨/🟥 V</TableHead>
                          <TableHead className="w-20">Jogado</TableHead>
                          <TableHead className="w-10"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {form.matches.map((m, idx) => (
                          <TableRow key={m.id}>
                            <TableCell className="text-xs text-muted-foreground">{idx + 1}</TableCell>
                            <TableCell>
                              <Select value={m.home_id} onValueChange={(v) => updMatch(m.id, { home_id: v })}>
                                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {form.teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Input className="h-8 w-12" type="number" min={0}
                                  value={m.home_score ?? ""} onChange={(e) => updMatch(m.id, { home_score: e.target.value === "" ? null : Number(e.target.value) })} />
                                <span>x</span>
                                <Input className="h-8 w-12" type="number" min={0}
                                  value={m.away_score ?? ""} onChange={(e) => updMatch(m.id, { away_score: e.target.value === "" ? null : Number(e.target.value) })} />
                              </div>
                            </TableCell>
                            <TableCell>
                              <Select value={m.away_id} onValueChange={(v) => updMatch(m.id, { away_id: v })}>
                                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {form.teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Input className="h-8 w-10" type="number" min={0}
                                  value={m.home_yellow} onChange={(e) => updMatch(m.id, { home_yellow: Number(e.target.value) || 0 })} />
                                <Input className="h-8 w-10" type="number" min={0}
                                  value={m.home_red} onChange={(e) => updMatch(m.id, { home_red: Number(e.target.value) || 0 })} />
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Input className="h-8 w-10" type="number" min={0}
                                  value={m.away_yellow} onChange={(e) => updMatch(m.id, { away_yellow: Number(e.target.value) || 0 })} />
                                <Input className="h-8 w-10" type="number" min={0}
                                  value={m.away_red} onChange={(e) => updMatch(m.id, { away_red: Number(e.target.value) || 0 })} />
                              </div>
                            </TableCell>
                            <TableCell>
                              <input type="checkbox" checked={m.played}
                                onChange={(e) => updMatch(m.id, { played: e.target.checked })} />
                            </TableCell>
                            <TableCell>
                              <Button type="button" variant="ghost" size="icon" onClick={() => removeMatch(m.id)}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <p className="text-xs text-muted-foreground mt-2">
                      Legenda: M = Mandante · V = Visitante · 🟨 Amarelos · 🟥 Vermelhos.
                      Marque "Jogado" para o placar contar na classificação.
                    </p>
                  </div>
                )}
              </fieldset>

              <Field label="Observações">
                <Textarea rows={2} value={form.notes} onChange={(e) => upd("notes", e.target.value)} />
              </Field>

              <div className="flex flex-wrap gap-2">
                <Button onClick={handleSave}>
                  <Save className="h-4 w-4 mr-1" />Salvar Campeonato
                </Button>
                <Button variant="outline" onClick={() => setStandingsOpen(true)}>
                  <Award className="h-4 w-4 mr-1" />Ver Classificação
                </Button>
                {form.id && (
                  <Button variant="ghost" onClick={() => setForm(emptyForm())}>Novo</Button>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="lista" className="mt-4 space-y-2">
          {items.length === 0 && (
            <p className="text-sm text-muted-foreground">Nenhum campeonato cadastrado.</p>
          )}
          <div className="grid md:grid-cols-2 gap-3">
            {items.map((it) => (
              <Card key={it.id}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-semibold">{it.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {it.event_type} · {it.format === "grupo_unico" ? "Grupo Único" : "Pontos Corridos"}
                      </div>
                    </div>
                    <Badge variant="secondary">{(it.teams ?? []).length} times</Badge>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => editItem(it)}>
                      <Pencil className="h-3 w-3 mr-1" />Editar
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(it.id)}>
                      <Trash2 className="h-3 w-3 mr-1" />Excluir
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={standingsOpen} onOpenChange={setStandingsOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TableIcon className="h-5 w-5" />Classificação — {form.name || "Campeonato"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-sm mb-2">Classificatória</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">#</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead>P</TableHead>
                    <TableHead>J</TableHead>
                    <TableHead>V</TableHead>
                    <TableHead>E</TableHead>
                    <TableHead>D</TableHead>
                    <TableHead>GP</TableHead>
                    <TableHead>GC</TableHead>
                    <TableHead>SG</TableHead>
                    <TableHead>🟨</TableHead>
                    <TableHead>🟥</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {standings.map((s, i) => (
                    <TableRow key={s.team_id}>
                      <TableCell className="font-semibold">{i + 1}º</TableCell>
                      <TableCell>{s.name}</TableCell>
                      <TableCell className="font-bold">{s.points}</TableCell>
                      <TableCell>{s.played}</TableCell>
                      <TableCell>{s.wins}</TableCell>
                      <TableCell>{s.draws}</TableCell>
                      <TableCell>{s.losses}</TableCell>
                      <TableCell>{s.gf}</TableCell>
                      <TableCell>{s.ga}</TableCell>
                      <TableCell>{s.gd}</TableCell>
                      <TableCell>{s.yellow}</TableCell>
                      <TableCell>{s.red}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-xs text-muted-foreground mt-2">
                Desempate: 1º pontos · 2º vitórias · 3º saldo · 4º gols pró · 5º menos vermelhos · 6º menos amarelos.
              </p>
            </div>

            {form.format === "pontos_corridos" && standings.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm mb-2">🏆 Classificação Final</h3>
                <ol className="list-decimal ml-5 text-sm space-y-1">
                  {standings.map((s, i) => {
                    const label = i === 0 ? "Campeão" : i === 1 ? "Vice" : i === 2 ? "3º lugar" : `${i + 1}º`;
                    return <li key={s.team_id}><b>{s.name}</b> — {label} ({s.points} pts)</li>;
                  })}
                </ol>
              </div>
            )}

            {form.format === "grupo_unico" && brackets.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm mb-2">Eliminatória</h3>
                <div className="grid md:grid-cols-3 gap-3">
                  {brackets.map((b) => (
                    <div key={b.label} className="border rounded-md p-3">
                      <div className="font-semibold text-sm mb-2">{b.label}</div>
                      <ol className="list-decimal ml-4 text-sm space-y-1">
                        {b.teams.map((t) => (
                          <li key={t.team_id}>{t.name} ({t.points} pts)</li>
                        ))}
                      </ol>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setStandingsOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}
